import { useQuery } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { getRequest, paginationMapper, serialize } from 'src/utils/request';

import type { RequestDataArray } from 'src/types/requestData';

const mapTutorialQuestions = (
  question: UCM.TutorialQuestionRawType,
  questionId: string,
): UCM.TutorialQuestionType => ({
  id: questionId,
  code: question.code,
  uuid: question.uuid,
  isVisible: question.is_visible,
  question: {
    de: question.question_de,
    en: question.question_en,
  },
  description: {
    de: question.description_de,
    en: question.description_en,
  },
  options: question.options.map((option) => ({
    id: option.id,
    code: option.code,
    uuid: option.uuid,
    isCorrect: option.is_correct,
    isVisible: option.is_visible,
    text: {
      de: option.text_de,
      en: option.text_en,
    },
  })),
  correctAnswerExplanation: {
    de: question.correct_answer_explanation_de,
    en: question.correct_answer_explanation_en,
  },
  tutorialUuid: question.tutorial_uuid,
  tutorialCode: question.tutorial_code,
});

const getTutorialQuestions = async (
  tutorialUuid: string,
  params: Record<string, string | number> = {},
) => {
  const query = serialize(params);

  const {
    data: { data, meta },
  } = await getRequest<
    RequestDataArray<UCM.TutorialQuestionRawType, UCM.MetaRawType>
  >(`${API.GET.tutorialQuestions(tutorialUuid)}?${query}`);

  return {
    meta: paginationMapper(meta),
    data: data.map(({ attributes, id }) =>
      mapTutorialQuestions(attributes, id),
    ),
  };
};

interface GetTutorialQuestionsProps {
  uuid: string;
  config?: { enabled?: boolean };
  params?: Record<string, string | number>;
}

export const useGetTutorialQuestions = ({
  uuid,
  params = {},
  config = { enabled: true },
}: GetTutorialQuestionsProps) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TUTORIAL_QUESTIONS, uuid, params],
    queryFn: () => getTutorialQuestions(uuid, params),
    staleTime: 1000 * 60 * 5, // Dashboards refreshed after every 5 minutes automatically
    ...config,
  });
};
